import { format, isValid, parse, parseISO } from 'date-fns';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { enAU } from 'date-fns/locale';

export const LOCALIZED_FORMAT = 'PP p';
export const LONG_FORMAT = 'dd/MM/yyyy hh:mm a';

export function formatDate(
  date: string | null | undefined,
  pattern = LOCALIZED_FORMAT
) {
  if (!date || typeof date !== 'string' || !date.trim()) {
    return '';
  }
  try {
    const parsed = parseISO(date);
    if (!isValid(parsed)) {
      return '';
    }
    return format(parsed, pattern, { locale: enAU });
  } catch (error) {
    console.warn('Invalid date format:', date, error);
    return '';
  }
}

export function formatDateInTimeZone(
  date: string | null | undefined,
  timeZone: string,
  pattern = LOCALIZED_FORMAT
) {
  if (!date || typeof date !== 'string' || !date.trim()) {
    return '';
  }
  try {
    const parsed = parseISO(date);
    if (!isValid(parsed)) {
      return '';
    }
    return formatInTimeZone(parsed, timeZone, pattern, { locale: enAU });
  } catch (error) {
    console.warn('Invalid date format:', date, error);
    return '';
  }
}

/**
 * Helper uses the localized date format but will attempt to remove the year if
 * it is the current year. Useful for cases where space is at a premium
 */
export function localizedShortDate(date: string) {
  return formatDate(date).replace(`, ${format(new Date(), 'y')}`, '');
}

/**
 * Formats a LocalTime string (HH:mm format) to a readable time format (h:mm a).
 *
 * Note: scheduledStartTime and scheduledEndTime are LocalTime fields already in the job's timezone.
 * This function just formats the time string for display - it does NOT perform timezone conversion.
 *
 * @param time - LocalTime string in HH:mm format (e.g., "15:00" for 3PM)
 * @returns Formatted time string (e.g., "3:00 PM")
 */
export function formatTime(time: string | null | undefined) {
  if (!time || typeof time !== 'string' || !time.trim()) {
    return '';
  }
  try {
    const parsed = parse(time, 'HH:mm', new Date());
    if (isNaN(parsed.getTime())) {
      return '';
    }
    return format(parsed, 'h:mm a');
  } catch (error) {
    console.warn('Invalid time format:', time, error);
    return '';
  }
}

/**
 * @deprecated DO NOT USE for display purposes!
 * Times should ALWAYS be displayed in the scheduled timezone, not the user's timezone.
 * Use formatTime() instead for displaying scheduled times.
 *
 * This function converts a LocalTime string (HH:mm) in a specific timezone to the user's browser timezone.
 * It exists for internal date/time calculations only, not for UI display.
 *
 * @param time - LocalTime string in HH:mm format (e.g., "15:00")
 * @param date - Date string in YYYY-MM-DD format (e.g., "2025-11-04")
 * @param jobTimeZone - IANA timezone identifier (e.g., "America/Sao_Paulo")
 * @returns Formatted time string in user's browser timezone (e.g., "4:00 AM")
 */
export function formatTimeInUserTimezone(
  time: string | null | undefined,
  date: string | Date | null | undefined,
  jobTimeZone: string | null | undefined
): string {
  if (!time || !date || !jobTimeZone) {
    return formatTime(time);
  }

  try {
    // Parse the date (handle both string and Date objects)
    let dateStr: string;
    if (typeof date === 'string') {
      dateStr = date;
    } else {
      // Validate Date object before formatting
      if (!date || !isValid(date)) {
        return formatTime(time);
      }
      // IMPORTANT: When we receive a Date object, formatting it directly with `format(date, 'yyyy-MM-dd')`
      // uses the browser's timezone. That can shift the calendar day for jobs in other timezones.
      // Instead, anchor the day in the job's timezone by formatting the Date in `jobTimeZone`.
      // This ensures the date portion we combine with the LocalTime string reflects the job's local day.
      dateStr = formatInTimeZone(date, jobTimeZone, 'yyyy-MM-dd');
    }

    // Validate date string format (should be YYYY-MM-DD)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return formatTime(time);
    }

    // Create a date-time string in the job's timezone: YYYY-MM-DDTHH:mm
    const dateTimeString = `${dateStr}T${time}`;

    // Convert from job timezone to UTC, then to browser's local timezone
    const utcDate = zonedTimeToUtc(dateTimeString, jobTimeZone);

    // Validate the converted date
    if (!isValid(utcDate)) {
      return formatTime(time);
    }

    // Format in the browser's local timezone
    return format(utcDate, 'h:mm a', { locale: enAU });
  } catch (error) {
    console.warn('Error converting time to user timezone:', error);
    return formatTime(time);
  }
}
